import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import styled from 'styled-components';
import { navDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';
import { EtherealShadow, GeometricPattern } from '@components/backgrounds';

// WebGL background - conditionally imported
let AnimatedBackground;
try {
  AnimatedBackground = require('@components/webgl').AnimatedBackground;
} catch (e) {
  AnimatedBackground = () => null;
}

const StyledHeroSection = styled(motion.section)`
  min-height: 100vh;
  height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  position: relative;
  overflow: hidden;
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    height: auto;
    min-height: 100vh;
  }

  /* WebGL Background */
  .webgl-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    opacity: 0.3;
    pointer-events: none;
  }

  /* Geometric Pattern Background */
  .geometric-pattern-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
  }

  /* Ethereal Shadow Background */
  .ethereal-shadow-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: none;
  }

  /* Left side - Text content */
  .hero-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: var(--space-5xl) var(--container-padding);
    position: relative;
    z-index: 2;

    @media (max-width: 968px) {
      padding: calc(var(--nav-height) + var(--space-4xl)) var(--container-padding) var(--space-4xl);
      min-height: 70vh;
    }
  }

  /* Right side - Visual element */
  .hero-visual {
    position: relative;
    background: var(--bg-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-left: 1px solid var(--border-color);

    @media (max-width: 968px) {
      min-height: 40vh;
      order: -1;
      border-left: none;
      border-bottom: 1px solid var(--border-color);
    }

    /* Large background text */
    &::before {
      content: 'PORTFOLIO';
      position: absolute;
      font-size: clamp(120px, 30vw, 400px);
      font-weight: 700;
      color: rgba(0, 0, 0, 0.02);
      letter-spacing: 0.1em;
      font-family: var(--font-display);
      text-transform: uppercase;
      white-space: nowrap;
      transform: rotate(-90deg);
      z-index: 1;
    }

    /* Decorative shapes - Japanese minimal */
    &::after {
      content: '';
      position: absolute;
      width: 200px;
      height: 200px;
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 50%;
      z-index: 1;
    }
  }

  h1 {
    margin: 0 0 var(--space-lg) 0;
    color: var(--text-tertiary);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    font-weight: 400;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  h2 {
    margin: 0;
    font-size: clamp(72px, 16vw, 180px);
    font-weight: 700;
    line-height: 0.85;
    letter-spacing: -0.05em;
    color: var(--text-primary);
    text-transform: uppercase;
    font-family: var(--font-display);
    position: relative;
    
    /* Black accent lines - Japanese minimal */
    &::after {
      content: '';
      position: absolute;
      bottom: -var(--space-lg);
      left: 0;
      width: 100px;
      height: 2px;
      background: var(--accent-primary);
    }

    &::before {
      content: '';
      position: absolute;
      bottom: calc(-var(--space-lg) - 8px);
      left: 0;
      width: 60px;
      height: 1px;
      background: var(--accent-primary);
      opacity: 0.5;
    }
  }

  h3 {
    margin: var(--space-2xl) 0 0 0;
    color: var(--text-secondary);
    line-height: 1.3;
    font-weight: 300;
    font-size: clamp(20px, 3vw, 32px);
    letter-spacing: 0.02em;
    opacity: 0.9;
  }

  p {
    margin: var(--space-xl) 0 0 0;
    max-width: 500px;
    color: var(--text-tertiary);
    line-height: 1.9;
    font-size: clamp(16px, 1.8vw, 20px);
    font-weight: 300;
    opacity: 0.85;
  }

  .cta-group {
    margin-top: var(--space-3xl);
    display: flex;
    gap: var(--space-lg);
    flex-wrap: wrap;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    background: var(--accent-primary);
    color: var(--bg-primary);
    border-color: var(--accent-primary);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    
    &:hover {
      background: var(--accent-secondary);
      border-color: var(--accent-secondary);
      transform: translateY(-2px);
      box-shadow: 0 8px 24px var(--shadow-md);
    }
  }

  .secondary-link {
    ${({ theme }) => theme.mixins.bigButton};
    background: transparent;
    color: var(--text-primary);
    border-color: var(--border-color);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    
    &:hover {
      border-color: var(--accent-primary);
      color: var(--accent-primary);
      background: var(--bg-secondary);
      transform: translateY(-2px);
    }
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99],
    },
  },
};

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useSpring(useTransform(scrollYProgress, [0, 0.5], [1, 0.98]), {
    stiffness: 100,
    damping: 30,
  });

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsMounted(true);
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, [prefersReducedMotion]);

  const content = (
    <motion.div
      className="hero-content"
      variants={containerVariants}
      initial="hidden"
      animate={isMounted ? 'visible' : 'hidden'}>
      <motion.h1 variants={itemVariants}>Hi, I'm</motion.h1>
      <motion.h2
        className="big-heading"
        variants={itemVariants}
        whileHover={{ scale: 1.02 }}
        transition={{ type: 'spring', stiffness: 400 }}>
        Niwant<br />Salunke
      </motion.h2>
      <motion.h3 variants={itemVariants}>Full-Stack & Applied AI/ML Engineer</motion.h3>
      <motion.p variants={itemVariants}>
        Full-Stack and Applied AI/ML Engineer experienced in building scalable, production-ready systems that integrate modern web technologies with multimodal machine learning. Strong background in real-time inference, cloud-native APIs, and AI-driven automation across enterprise and research environments.
      </motion.p>
      <motion.div className="cta-group" variants={itemVariants}>
        <motion.a
          className="email-link"
          href="/Niwant_Resume_FS.pdf"
          target="_blank"
          rel="noreferrer"
          whileHover={{ scale: 1.05, y: -3 }}
          whileTap={{ scale: 0.95 }}>
          View Resume
        </motion.a>
        <motion.a
          className="secondary-link"
          href="#projects"
          whileHover={{ scale: 1.05, y: -3 }}
          whileTap={{ scale: 0.95 }}>
          View Work
        </motion.a>
      </motion.div>
    </motion.div>
  );

  const visual = (
    <motion.div
      className="hero-visual"
      initial={{ opacity: 0, x: 50 }}
      animate={isMounted ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
      transition={{ duration: 1, ease: [0.6, -0.05, 0.01, 0.99] }}>
      <div className="geometric-pattern-bg">
        <GeometricPattern
          opacity={1}
          shapesOpacity={0.4}
          shapeColor="rgba(0, 0, 0, 0.25)"
          dotColor="rgba(0, 0, 0, 0.3)"
        />
      </div>
      <div className="ethereal-shadow-bg">
        <EtherealShadow
          color="rgba(0, 0, 0, 0.12)"
          animation={{ scale: 80, speed: 60 }}
          noise={{ opacity: 0.2, scale: 1.2 }}
          sizing="fill"
          opacity={0.8}
        />
      </div>
      <div className="webgl-background">
        <AnimatedBackground />
      </div>
    </motion.div>
  );

  return (
    <StyledHeroSection style={{ opacity, scale }}>
      {content}
      {visual}
    </StyledHeroSection>
  );
};

export default Hero;
